import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Divider,
    Chip,
    Stack,
    Paper,
    Avatar,
    Alert,
    Snackbar,
} from "@mui/material";
import {
    GoogleMap,
    LoadScript,
    Marker,
    Polyline,
    useJsApiLoader,
} from "@react-google-maps/api";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import OpacityIcon from "@mui/icons-material/Opacity";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ImageSlider from "../routes/ImageSlider";
import { get_places_tourist_attraction_details_guest } from "../../api/API";


function PlacesDetails() {
    // http://localhost:8080/get_places_tourist_attraction_details
    const { t, i18n } = useTranslation();
    const mapContainerStyle = { width: "100%", height: "500px" };
    const navigate = useNavigate();
    const [placesDataDetail, setPlacesDataDetail] = useState([]);
    const token = localStorage.getItem("token");
    const [loading, setLoading] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState({
        open: false,
        message: "",
    });
    const mapRef = useRef(null);
    const location = useLocation();
    const stateDetail = location.state;
    const BASE_URL = "http://localhost:8089";
    const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    });
    const [points, setPoints] = useState([]);

    useEffect(() => {
        LoadDataPlaceDetail(); // โหลดข้อมูลใหม่ทุกครั้งที่ภาษาเปลี่ยน
    }, [i18n.language]);

    const LoadDataPlaceDetail = async () => {
        setLoading(true);

        try {
            const res = await axios.post(get_places_tourist_attraction_details_guest, { ...stateDetail, language: i18n.language }, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            // console.log("res.data Details", res.data);

            setPlacesDataDetail(res.data);
            localStorage.setItem("placesDataDetail", JSON.stringify(res.data)); // เซฟไว้ใช้ครั้งหน้า
            // console.log("res.data Details", res.data);

            // แปลงพิกัด map
            const coordinateStr = res.data.googleMapCoordinates;
            if (coordinateStr) {
                const [latStr, lngStr] = coordinateStr.split(',').map(s => s.trim());
                const lat = parseFloat(latStr);
                const lng = parseFloat(lngStr);

                if (!isNaN(lat) && !isNaN(lng)) {

                    setPoints([
                        {
                            lat: lat,
                            lng: lng,
                            name: res.data.attractionName,
                        }
                    ]);
                }
            }
        } catch (err) {
            console.error("Fetch failed:", err);
            setErrorSnackbar({
                open: true,
                message: t("load_failed"),
            });
        } finally {
            setLoading(false);
        }
    };

    const EnergyItem = ({ icon, label, value, unit, color }) => (
        <Card
            sx={{
                p: 2,
                borderRadius: 3,
                flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1 1 30%" },
                bgcolor: "#fffdf8",
            }}
            elevation={2}
        >
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Avatar sx={{ bgcolor: color, flexShrink: 0 }}>{icon}</Avatar>
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            </Stack>
            <Typography sx={{ fontWeight: "bold", fontSize: { xs: "0.95rem", sm: "1.05rem" }, wordBreak: "break-word" }}>
                {value}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                    {unit}
                </Typography>
            </Typography>
        </Card>
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <ImageSlider
                        images={placesDataDetail.images}
                        BASE_URL={BASE_URL}
                    />
                    {/* google map */}
                    {isLoaded && (
                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: "2px solid #81c784",
                                    backgroundColor: "#e8f5e9",
                                    overflow: "hidden",
                                    height: "500px", // ต้องกำหนดความสูงให้ map แสดงผล
                                }}
                            >
                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={points[0]}
                                    zoom={14}
                                    onLoad={(map) => (mapRef.current = map)}
                                >
                                    {points?.map((p, idx) => (
                                        <Marker
                                            key={idx}
                                            position={{ lat: p.lat, lng: p.lng }}
                                            label={`${idx + 1}`}
                                            title={p.name}
                                        />
                                    ))}
                                </GoogleMap>
                            </Box>
                        </Grid>
                    )}
                </Grid>
                {/* ข้อมูลรายละเอียด */}
                <Grid item xs={12} md={6}>
                    <Typography sx={{ fontWeight: "bold", fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.125rem" } }} gutterBottom>
                        {placesDataDetail?.attractionName}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {t("history_title")}
                    </Typography>

                    {/* ย่อหน้า 1 */}
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {placesDataDetail?.historyDescription}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" fontWeight="bold">
                        {t("activities_title")}
                    </Typography>
                    <Typography> {placesDataDetail?.activities}</Typography>

                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                        {t("fee_title")}
                    </Typography>
                    <Typography>{placesDataDetail?.cost}</Typography>

                    <Divider sx={{ my: 3 }} />
                    {/* ข้อมูลการใช้งาน */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom>
                            {t("place_details_title")}
                        </Typography>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={2}
                                    sx={{ p: 2, textAlign: "center", display: "flex", flexDirection: "column", gap: 0.5 }}
                                >
                                    <Typography color="primary" sx={{ fontWeight: "bold", fontSize: { xs: "1.1rem", sm: "1.25rem" }, wordBreak: "break-word" }}>
                                        {placesDataDetail?.touristCapacity}
                                    </Typography>
                                    <Typography variant="body2">{t("tourist_measurement")}</Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={2}
                                    sx={{ p: 2, textAlign: "center", display: "flex", flexDirection: "column", gap: 0.5 }}
                                >
                                    <Typography sx={{ fontWeight: "bold", fontSize: { xs: "1rem", sm: "1.1rem" }, wordBreak: "break-word" }}>
                                        {placesDataDetail?.openingHours}
                                    </Typography>
                                    <Typography variant="body2">{t("opening_hours")}</Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={2}
                                    sx={{ p: 2, textAlign: "center", display: "flex", flexDirection: "column", gap: 0.5 }}
                                >
                                    <Typography sx={{ fontWeight: "bold", fontSize: { xs: "1rem", sm: "1.1rem" }, wordBreak: "break-word" }}>
                                        {placesDataDetail?.touristSeason}
                                    </Typography>
                                    <Typography variant="body2">{t("tourist_season")}</Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={2}
                                    sx={{ p: 2, textAlign: "center", display: "flex", flexDirection: "column", gap: 0.5 }}
                                >
                                    <Typography sx={{ fontWeight: "bold", fontSize: { xs: "1rem", sm: "1.1rem" }, wordBreak: "break-word" }}>
                                        {placesDataDetail?.carFootprintPerDay}
                                    </Typography>
                                    <Typography variant="body2">{t("carbon_footprint_per_day")}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* ข้อมูลสิ่งแวดล้อม */}
                    <Divider sx={{ my: 3 }} />
                    <Grid item xs={12}>
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {t("energy_usage_title")}
                            </Typography>

                            <Stack
                                direction="row"
                                spacing={2}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{ mb: 3 }}
                            >
                                <EnergyItem
                                    icon={<FlashOnIcon />}
                                    label={t("electricity_usage")}
                                    value={placesDataDetail?.electricityUsage}
                                    unit={t("unit_unit")}
                                    color="orange"
                                />
                                <EnergyItem
                                    icon={<OpacityIcon />}
                                    label={t("water_usage")}
                                    value={placesDataDetail?.waterUsage}
                                    unit={t("unit_liter")}
                                    color="skyblue"
                                />
                                <EnergyItem
                                    icon={<LocalGasStationIcon />}
                                    label={t("fuel_usage")}
                                    value={placesDataDetail?.fuelUsage}
                                    unit={t("unit_liter_per_day")}
                                    color="gray"
                                />
                            </Stack>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                {t("wastewater_management")}
                            </Typography>
                            <Typography>{placesDataDetail?.wastewaterManagement}</Typography>

                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                                {t("waste_management")}
                            </Typography>
                            <Typography>{placesDataDetail?.wasteManagement}</Typography>

                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                                {t("carbon_emission_place")}
                            </Typography>
                            <Typography>
                                {placesDataDetail?.ecoSystemChange}
                            </Typography>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="h6" sx={{ mt: 4 }}>
                                {t("contact_info_title")}
                            </Typography>
                            <Typography>{placesDataDetail?.contactInfo}</Typography>

                            <Typography variant="h6" sx={{ mt: 2 }}>
                                {t("location_title")}
                            </Typography>
                            <Typography>
                                {placesDataDetail?.locationDescription}
                            </Typography>

                            <Typography variant="h6" sx={{ mt: 2 }}>
                                {t("notes_title")}
                            </Typography>
                            <Typography>{placesDataDetail?.notes}</Typography>
                        </Box>
                    </Grid>


                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            mt: 4,
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PersonIcon fontSize="small" />
                                <Typography variant="body2">Admin</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CalendarMonthIcon fontSize="small" />
                                <Typography variant="body2">{t("months_ago_7")}</Typography>
                            </Stack>
                        </Stack>
                    </Box>

                </Grid>

            </Grid>
            {/* // JSX ส่วนแสดง error Snackbar */}
            <Snackbar
                open={errorSnackbar.open}
                autoHideDuration={3000}
                onClose={() => setErrorSnackbar({ open: false, message: "" })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity="error"
                    variant="filled"
                    sx={{ fontWeight: "bold" }}
                >
                    {errorSnackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PlacesDetails